import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from '../ui/Button';

/**
 * Reusable layout for list pages with filtering, searching, and sorting
 *
 * @param {object} props
 * @param {array} props.items - Array of items to display
 * @param {array} props.filters - Filter definitions
 *   Each filter: { key, label, type, options?, getOptions? }
 *   type: 'search' | 'select' | 'multi-select'
 * @param {function} props.renderItem - Function to render each item
 * @param {function} props.filterFn - Custom filter function (optional)
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle
 * @param {boolean} props.isLoading - Loading state
 * @param {object} props.error - Error object
 * @param {function} props.onRetry - Retry function on error
 */
export default function FilterableList({
  items = [],
  filters = [],
  renderItem,
  filterFn = null,
  title,
  subtitle,
  isLoading = false,
  error = null,
  onRetry = null,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Build current filter values from URL params
  const currentFilters = useMemo(() => {
    const result = {};
    filters.forEach((f) => {
      result[f.key] = searchParams.get(f.key) ?? f.defaultValue ?? '';
    });
    return result;
  }, [searchParams, filters]);

  // Update a single filter
  const updateFilter = (key, value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const filter = filters.find((f) => f.key === key);
        const defaultVal = filter?.defaultValue ?? '';

        if (value === defaultVal) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true }
    );
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.some((f) => {
      const val = currentFilters[f.key];
      const defaultVal = f.defaultValue ?? '';
      return val !== defaultVal;
    });
  }, [currentFilters, filters]);

  // Clear all filters
  const clearFilters = () => {
    setSearchParams({}, { replace: true });
  };

  // Apply filtering and custom filter function
  const filtered = useMemo(() => {
    let result = items;

    // Apply built-in filter logic (can be overridden by filterFn)
    if (!filterFn) {
      result = items.filter((item) => {
        return filters.every((filter) => {
          const filterValue = currentFilters[filter.key];
          if (!filterValue || filterValue === filter.defaultValue) return true;

          if (filter.type === 'search') {
            return filter.searchFn(item, filterValue);
          } else if (filter.type === 'select') {
            return filter.getValue(item) === filterValue;
          }
          return true;
        });
      });
    } else {
      // Use custom filter function
      result = items.filter((item) => filterFn(item, currentFilters));
    }

    return result;
  }, [items, filters, currentFilters, filterFn]);

  const resultCount = filtered.length;
  const itemCount = items.length;

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      <div className="container-custom">
        {/* Title */}
        {title && (
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2">{title}</h1>
            {subtitle && <p className="text-neutral-600">{subtitle}</p>}
          </div>
        )}

        {/* Filters */}
        {filters.length > 0 && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-4 mb-6 shadow-sm">
            <div className="flex flex-col sm:flex-row gap-3">
              {filters.map((filter) => (
                <div key={filter.key} className="flex-1">
                  {filter.type === 'search' && (
                    <div className="relative">
                      <input
                        type="text"
                        value={currentFilters[filter.key]}
                        onChange={(e) => updateFilter(filter.key, e.target.value)}
                        placeholder={filter.label}
                        className="w-full pl-4 pr-9 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <svg
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  )}

                  {filter.type === 'select' && (
                    <select
                      value={currentFilters[filter.key]}
                      onChange={(e) => updateFilter(filter.key, e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {(filter.options || filter.getOptions?.() || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {filter.type === 'tabs' && (
                    <div className="flex gap-1 border-b border-neutral-200">
                      {(filter.options || filter.getOptions?.() || []).map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => updateFilter(filter.key, opt.value)}
                          className={`px-3 py-2 text-sm font-medium transition-colors ${
                            currentFilters[filter.key] === opt.value
                              ? 'text-primary-600 border-b-2 border-primary-600'
                              : 'text-neutral-600 hover:text-neutral-900'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {hasActiveFilters && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-neutral-500">
                  {resultCount} result{resultCount !== 1 ? 's' : ''} of {itemCount}
                </span>
                <button
                  onClick={clearFilters}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-neutral-200 p-5 animate-pulse"
              >
                <div className="h-4 bg-neutral-200 rounded w-1/3 mb-3" />
                <div className="h-5 bg-neutral-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-neutral-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-16">
            <p className="text-neutral-600 mb-4">Failed to load items.</p>
            {onRetry && (
              <Button variant="outline" onClick={onRetry}>
                Try Again
              </Button>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && resultCount === 0 && (
          <div className="text-center py-16">
            <svg
              className="w-12 h-12 text-neutral-300 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-neutral-600 mb-3">No items match your filters.</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Results Grid/List */}
        {!isLoading && !error && resultCount > 0 && (
          <>
            {!hasActiveFilters && (
              <p className="text-sm text-neutral-500 mb-4">
                Showing {resultCount} item{itemCount !== 1 ? 's' : ''}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((item, idx) => (
                <div key={item.id ?? idx}>{renderItem(item)}</div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
